import type { ReviewStats } from '../../types/dashboard'

interface MetricCardsProps {
  stats: ReviewStats | null
  isLoading: boolean
}

export interface CardProps {
  label: string
  value: string | number
  sub?: string
  valueColor?: string
}

export function Card({ label, value, sub, valueColor = 'text-rd-text' }: CardProps) {
  return (
    <div className="bg-rd-surface border border-white/[0.07] rounded-xl p-4">
      <p className="text-xs text-rd-muted mb-1.5">{label}</p>
      <p className={`text-2xl font-semibold font-mono ${valueColor}`}>{value}</p>
      {sub && <p className="text-[10px] text-rd-dim mt-1">{sub}</p>}
    </div>
  )
}

export default function MetricCards({ stats, isLoading }: MetricCardsProps) {
  if (isLoading || !stats) {
    return (
      <div className="grid grid-cols-5 gap-2.5 flex-shrink-0">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="bg-rd-surface border border-white/[0.07] rounded-xl p-4 animate-pulse">
            <div className="h-3 w-24 bg-white/5 rounded mb-3" />
            <div className="h-7 w-16 bg-white/5 rounded" />
          </div>
        ))}
      </div>
    )
  }

  const pending = stats.by_status['pending_approval'] ?? 0

  return (
    <div className="grid grid-cols-5 gap-2.5 flex-shrink-0">
      <Card
        label="Total reviews"
        value={stats.total_reviews}
        sub="All time"
      />
      <Card
        label="Pending approval"
        value={pending}
        sub="Awaiting your action"
        valueColor={pending > 0 ? 'text-warning-500' : 'text-rd-text'}
      />
      <Card
        label="Posted"
        value={stats.total_responded}
        sub="Responses live"
        valueColor={stats.total_responded > 0 ? 'text-rd-accent' : 'text-rd-text'}
      />
      <Card
        label="Escalated"
        value={stats.total_escalated}
        sub="Need your attention"
        valueColor={stats.total_escalated > 0 ? 'text-danger-500' : 'text-rd-text'}
      />
      <Card
        label="Avg. rating"
        value={stats.average_rating > 0 ? stats.average_rating.toFixed(1) : '—'}
        sub="Out of 5.0"
      />
    </div>
  )
}
